import { useQuery, useSuspenseQuery, type UseQueryOptions } from "@tanstack/react-query";
import type { ConnectionSchemas } from "api-types";
import { axiosClient, useAxios } from "auth-sdk";
import type { AxiosError, AxiosResponse } from "axios";
import { useSearchParams } from "react-router";

import type { ConnectorPropertiesSchema } from "../data-sources/schemas";

import type { AuthCallbackPath } from "./callback-paths";

/**
 * Fetch the connection properties for a single connector.
 *
 * `connectorsApiUrl` is a customer-scoped, `:customerSlug`-templated endpoint
 * (e.g. `/api/:customerSlug/funding/api/connectors/`). It is passed in rather
 * than hard-coded so the same side-panel flows can be driven from different
 * surfaces (onboarding, settings/profile) with their own callback paths.
 */
export const usePlatformConnectorProperties = (
  customerDomain: string,
  connectorSlug: string,
  postConnectPath: AuthCallbackPath,
  connectorsApiUrl: string
) => {
  const client = useAxios();

  const base = connectorsApiUrl.replace(":customerSlug", customerDomain);
  const fetchUrl = `${base.replace(/\/$/, "")}/${connectorSlug}`;

  const params = new URLSearchParams({ callbackPath: postConnectPath });
  const queryFn = () => client.get<ConnectorPropertiesSchema>(fetchUrl, { params });

  return useSuspenseQuery({
    queryKey: [fetchUrl, postConnectPath],
    queryFn,
    select: ({ data }) => data,
    refetchOnMount: "always",
  });
};

/**
 * Fetch the complete-connection props after an OAuth redirect (used to render
 * the post-connect account/property selector). `completeConnectionApiUrl` is a
 * `:customerSlug`-templated endpoint passed in per surface. Generic over the
 * response schema so callers can retain their app-specific type.
 */
export const useCompleteConnectionProps = <T = ConnectionSchemas.CompleteConnectionPropsSchema>(
  customerDomain: string,
  completeConnectionApiUrl: string,
  options: Omit<
    UseQueryOptions<AxiosResponse<T>, AxiosError, T>,
    "queryKey" | "queryFn" | "select" | "refetchOnMount"
  > = {}
) => {
  const [searchParams] = useSearchParams();
  const fetchUrl = completeConnectionApiUrl.replace(":customerSlug", customerDomain);
  // Retain the original search params as these are used on the BE
  const queryFn = () => axiosClient.get<T>(fetchUrl, { params: searchParams });
  return useQuery({
    ...options,
    queryKey: [fetchUrl, searchParams.toString()],
    queryFn,
    select: ({ data }) => data,
    refetchOnMount: "always",
  });
};
